/**
 * Label for a collapsed-rail nav row. Right of the rail, dark, on hover and
 * keyboard focus — mandatory whenever the sidebar is collapsed (see
 * assets/handoffs/design_handoff_app_shell, Rules — collapse).
 *
 * No longer a thin composition over theme/untitled's Tooltip — that
 * component positions itself with `position: absolute` relative to its
 * trigger, which sits inside Sidebar's `.nav` (`overflow-y: auto`). Per the
 * CSS overflow spec, setting one axis to a non-`visible` value forces the
 * other to compute as `auto` too, so the tooltip gets clipped (and the nav
 * gains a phantom horizontal scrollbar) the moment it overflows sideways —
 * the same root cause NavFlyout hit and fixed the same way (see
 * SidebarNavParent.jsx): portal to <body> and position in viewport
 * coordinates instead of relying on in-flow absolute positioning.
 *
 * @param {string} label - the nav item's own label, verbatim
 * @param {ReactElement} children - the trigger (nav row)
 */
import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./SidebarTooltip.module.scss";

const OPEN_DELAY = 300;
const CLOSE_DELAY = 100;

const SidebarTooltip = ({ label, children }) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);

  const show = () => {
    clearTimeout(closeTimer.current);
    openTimer.current = setTimeout(() => setVisible(true), OPEN_DELAY);
  };

  const hide = () => {
    clearTimeout(openTimer.current);
    closeTimer.current = setTimeout(() => setVisible(false), CLOSE_DELAY);
  };

  useLayoutEffect(() => {
    if (!visible) return;

    const measure = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect) setPosition({ top: rect.top + rect.height / 2, left: rect.right + 8 });
    };

    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [visible]);

  const existingRef = children.ref;
  const trigger = React.cloneElement(children, {
    ref: (node) => {
      triggerRef.current = node;
      if (typeof existingRef === "function") existingRef(node);
      else if (existingRef) existingRef.current = node;
    },
    onMouseEnter: (e) => {
      children.props.onMouseEnter?.(e);
      show();
    },
    onMouseLeave: (e) => {
      children.props.onMouseLeave?.(e);
      hide();
    },
    onFocus: (e) => {
      children.props.onFocus?.(e);
      show();
    },
    onBlur: (e) => {
      children.props.onBlur?.(e);
      hide();
    },
    onKeyDown: (e) => {
      children.props.onKeyDown?.(e);
      if (e.key === "Escape") {
        clearTimeout(openTimer.current);
        setVisible(false);
      }
    },
  });

  return (
    <>
      {trigger}
      {visible &&
        position &&
        createPortal(
          <span
            role="tooltip"
            className={styles.tooltip}
            style={{ top: position.top, left: position.left, transform: "translateY(-50%)" }}
          >
            {label}
          </span>,
          document.body
        )}
    </>
  );
};

export default SidebarTooltip;
